import { ArrowRight, Download } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { checklistPreviewContent } from "@/app/content/landingContent";
import { SITE_CONFIG } from "@/config/constants";
import { CONTACT_EMAIL_REGEX } from "../../../shared/contactFormValidation";

function useFocusOnMount<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const trigger = useCallback(() => {
    // Delay to ensure element is in DOM after state change
    requestAnimationFrame(() => ref.current?.focus());
  }, []);
  return { ref, trigger };
}

type FormState = "idle" | "pending" | "success" | "error";
type FieldErrors = Partial<Record<"email" | "consent", string>>;

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

function useTurnstile(containerId: string) {
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string>("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Load script if not already loaded
    const scriptId = "cf-turnstile-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    function initWidget() {
      const container = document.getElementById(containerId);
      if (!container || !window.turnstile) return;
      if (widgetIdRef.current !== null) return;

      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: SITE_CONFIG.turnstileSiteKey,
        callback: (token: string) => {
          tokenRef.current = token;
          setReady(true);
        },
        "expired-callback": () => {
          tokenRef.current = "";
          setReady(false);
        },
        "error-callback": () => {
          tokenRef.current = "";
          setReady(false);
        },
        theme: "light",
        size: "flexible",
      });
    }

    // If turnstile is already loaded
    if (window.turnstile) {
      initWidget();
    } else {
      (window as unknown as Record<string, unknown>).onTurnstileLoad = initWidget;
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [containerId]);

  const reset = useCallback(() => {
    tokenRef.current = "";
    setReady(false);
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return { token: tokenRef, ready, reset };
}

export function ChecklistPreviewSection() {
  const content = checklistPreviewContent;
  const form = content.downloadForm;

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [globalError, setGlobalError] = useState("");

  const { token: turnstileToken, reset: resetTurnstile } = useTurnstile("checklist-turnstile");
  const emailInputRef = useRef<HTMLInputElement>(null);
  const consentInputRef = useRef<HTMLInputElement>(null);
  const { ref: successRef, trigger: focusSuccess } = useFocusOnMount<HTMLParagraphElement>();

  function validateClient(): FieldErrors {
    const errors: FieldErrors = {};
    const trimmed = email.trim();
    if (!trimmed) {
      errors.email = form.emailError;
    } else if (!CONTACT_EMAIL_REGEX.test(trimmed)) {
      errors.email = form.emailError;
    }
    if (!consent) {
      errors.consent = form.consentError;
    }
    return errors;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setGlobalError("");

    const clientErrors = validateClient();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      // Focus the first field with an error
      requestAnimationFrame(() => {
        if (clientErrors.email) {
          emailInputRef.current?.focus();
        } else if (clientErrors.consent) {
          consentInputRef.current?.focus();
        }
      });
      return;
    }

    setFormState("pending");
    setFieldErrors({});

    try {
      const res = await fetch("/api/checklist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          consent,
          website: honeypot, // honeypot field
          turnstileToken: turnstileToken.current,
        }),
      });

      const data = await res.json() as {
        success?: boolean;
        error?: string;
        fieldErrors?: FieldErrors;
      };

      if (!res.ok) {
        if (data.fieldErrors) {
          setFieldErrors(data.fieldErrors);
          setFormState("idle");
        } else {
          setGlobalError(data.error ?? form.submitError);
          setFormState("error");
          resetTurnstile();
        }
        return;
      }

      setFormState("success");
      focusSuccess();
    } catch {
      setGlobalError(form.submitError);
      setFormState("error");
      resetTurnstile();
    }
  }

  return (
    <section
      id={content.id}
      className="py-20 md:py-24 px-6 bg-white"
      aria-labelledby="checklist-heading"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 max-w-3xl">
          <h2
            id="checklist-heading"
            className="text-[clamp(1.875rem,4vw,3rem)] text-slate-900 mb-6 tracking-tight font-bold"
          >
            {content.heading}
          </h2>
          <p className="text-[clamp(1.125rem,2vw,1.25rem)] text-slate-600 leading-relaxed max-w-prose">
            {content.description}
          </p>
        </div>

        {/* PDF download form — above the list */}
        <div className="mb-14 max-w-2xl rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8">
          <div className="flex items-start gap-4 mb-1">
            <Download size={22} className="text-[#004f4f] flex-shrink-0 mt-0.5" aria-hidden="true" />
            <h3 id="checklist-form-heading" className="font-semibold text-slate-900 text-lg">{form.heading}</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5 ml-[38px]">{form.description}</p>

          {formState === "success" ? (
            <p ref={successRef} tabIndex={-1} role="status" className="text-sm font-medium text-[#004f4f] ml-[38px] focus:outline-none">
              {form.submitSuccess}
            </p>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="ml-[38px]" aria-labelledby="checklist-form-heading">
              {/* Honeypot — hidden from humans, visible to bots */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
              >
                <label htmlFor="checklist-website">Website</label>
                <input
                  id="checklist-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <div className="flex-1">
                  <label htmlFor="checklist-email" className="block text-sm font-medium text-slate-700 mb-1">
                    {form.emailLabel}
                  </label>
                  <input
                    ref={emailInputRef}
                    id="checklist-email"
                    type="email"
                    autoComplete="email"
                    required
                    aria-required="true"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={form.emailPlaceholder}
                    aria-describedby={fieldErrors.email ? "checklist-email-error" : undefined}
                    aria-invalid={fieldErrors.email ? "true" : undefined}
                    disabled={formState === "pending"}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004f4f] focus:border-transparent disabled:opacity-50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={formState === "pending"}
                  className="inline-flex items-center gap-2 justify-center bg-[#004f4f] text-white px-6 py-3 rounded-xl hover:bg-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-2 transition-colors font-bold text-sm disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {formState === "pending" ? form.submitPending : form.submitIdle}
                </button>
              </div>

              {fieldErrors.email && (
                <p id="checklist-email-error" role="alert" className="mb-2 text-xs text-red-600">
                  {fieldErrors.email}
                </p>
              )}

              <div className="mb-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    ref={consentInputRef}
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    aria-required="true"
                    aria-describedby={fieldErrors.consent ? "checklist-consent-error" : undefined}
                    aria-invalid={fieldErrors.consent ? "true" : undefined}
                    disabled={formState === "pending"}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#004f4f] focus:ring-[#004f4f] disabled:opacity-50"
                  />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    {form.consentTextStart}
                    <a href={form.consentLinkHref} className="underline hover:text-slate-900">
                      {form.consentLinkLabel}
                    </a>
                    {form.consentTextEnd}
                  </span>
                </label>
                {fieldErrors.consent && (
                  <p id="checklist-consent-error" role="alert" className="mt-1 text-xs text-red-600">
                    {fieldErrors.consent}
                  </p>
                )}
              </div>

              {/* Cloudflare Turnstile */}
              <div id="checklist-turnstile" className="mb-3" />

              <p aria-live="assertive" aria-atomic="true" className={`text-xs text-red-600 ${globalError ? "" : "sr-only"}`}>
                {globalError}
              </p>
            </form>
          )}
        </div>

        <ol className="space-y-5 mb-12" aria-label="Lista najczęstszych błędów dostępności">
          {content.items.map((item) => (
            <li
              key={item.rank}
              className="flex gap-6 items-start p-6 rounded-2xl border border-gray-100 bg-gray-50"
            >
              <span
                className="text-3xl font-bold text-gray-200 leading-none flex-shrink-0 w-10 text-center select-none"
                aria-hidden="true"
              >
                {item.rank}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-3 mb-2">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900">{item.title}</h3>
                  <span className="text-xs font-semibold text-[#004f4f] bg-[#004f4f]/10 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {item.stat}
                  </span>
                </div>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed">{item.detail}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Consultation CTA — standalone at the bottom */}
        <a
          href={content.cta.href}
          className="inline-flex items-center gap-3 bg-[#004f4f] text-white px-8 py-4 rounded-full hover:bg-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 transition-colors font-bold text-base"
        >
          {content.cta.label}
          <ArrowRight size={20} aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}
